import { Apple, Google, Mail, Storefront, VolunteerActivism } from "@mui/icons-material";
import { Chip, IconButton, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { Datagrid, FunctionField, List, ReferenceField, SearchInput, SelectArrayInput, TextField } from "react-admin";
import { stripe_link } from "../api";
import CardUpdateEmailDialog from "../components/CardUpdateEmailDialog";
import Currency from "../components/currency";
import EmailWithCopy from "../components/EmailWithCopy";
import LocalisedDateField from "../components/localisedDateField";
import StartDateEndDateInput from "../components/startDateEndDateInput";
import TextWithCopy from "../components/TextWithCopy";

const EmptySubscriptionList = () => {
  return (
    <small>
      <i>This user has no subscriptions</i>
    </small>
  );
};

const SubscriptionStoreIcon = ({ store }: any) => {
  switch (store) {
    case "google":
      return <Google titleAccess="Google Play" />;
    case "apple":
      return <Apple titleAccess="Apple App Store" />;
    case "stripe":
      return <Storefront titleAccess="Playbrush Webshop" />;
    case "free":
      return <VolunteerActivism titleAccess="Free Subscription" />;
    default:
      return store;
  }
};

const SendEmailButton = ({ record }: { record: any }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const isStripeProvider = (record?.provider || "").toLowerCase() === "stripe";
  const canSendEmail = isStripeProvider && !!record?.stripeCustomerId;

  return (
    <>
      <Tooltip
        title={
          isStripeProvider ? "Send card update email" : "Card update email is only available for Stripe subscriptions"
        }
      >
        <span>
          <IconButton size="small" color="primary" onClick={() => setDialogOpen(true)} disabled={!canSendEmail}>
            <Mail fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {canSendEmail && (
        <CardUpdateEmailDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          stripeCustomerId={record.stripeCustomerId}
          subscriptionId={record.subscriptionId}
        />
      )}
    </>
  );
};

const subscriptionFilters = [
  <SearchInput source="customer_email__icontains" alwaysOn={true} placeholder="Customer Email" />,
  <SelectArrayInput
    label="Provider"
    source="provider__in"
    choices={[
      { id: "stripe", name: "Stripe" },
      { id: "apple", name: "Apple" },
      { id: "google", name: "Google" },
    ]}
    alwaysOn
    disableValue=""
  />,
  <StartDateEndDateInput source="createdAt" label="Created At" />,
  <StartDateEndDateInput source="currentPeriodStart" label="Current Period Start" />,
  <StartDateEndDateInput source="currentPeriodEnd" label="Current Period End" />,
];

const SubscriptionDataGrid = (props: any) => {
  return (
    <Datagrid bulkActionButtons={false} empty={<EmptySubscriptionList />}>
      {props.showEmail ? <EmailWithCopy source="customerEmail" label="Customer Email" /> : false}
      {props.showEmail ? (
        <ReferenceField
          source="userId"
          reference="users"
          link={(record: any, reference: any) => `/${reference}/${record.id}/show/subscriptions`}
        >
          <FunctionField render={(record: any) => `${record.firstName} ${record.lastName}`} />
        </ReferenceField>
      ) : (
        false
      )}
      <TextField source="title" />
      <TextField source="productType" />
      <FunctionField
        label="Subscription ID"
        render={(subscription: { subscriptionId: any }) => {
          return subscription.subscriptionId.startsWith("sub_") ? (
            <>
              <TextWithCopy source="subscriptionId" displayText="Subscription ID" />
              <a
                href={`${stripe_link}subscriptions/${subscription.subscriptionId}`}
                className="ex-link"
                target="_blank"
                title="View subscription on Stripe"
              >
                ↗
              </a>
            </>
          ) : (
            <>{subscription.subscriptionId}</>
          );
        }}
      />
      <FunctionField
        label="Provider"
        render={(subscription: { provider: string }) => {
          return <SubscriptionStoreIcon store={subscription.provider} />;
        }}
      />
      <FunctionField
        label="Status"
        render={(sub: any) => {
          return (
            <Chip
              label={sub.status}
              color={sub.status === "active" || sub.status === "trialing" ? "success" : "warning"}
              size="small"
              variant="outlined"
            />
          );
        }}
      />
      <FunctionField
        label="Start"
        render={(sub: any) => {
          return <LocalisedDateField record={sub} field="currentPeriodStart" compact={true} />;
        }}
      />
      <FunctionField
        label="End"
        render={(sub: any) => {
          return <LocalisedDateField record={sub} field="endDate" compact={true} />;
        }}
      />
      <FunctionField
        label="Price"
        render={(sub: any) => {
          return <Currency price={sub.price} currency={sub.currency || "eur"} />;
        }}
      />
      <FunctionField
        label=""
        render={(sub: any) => {
          return <SendEmailButton record={sub} />;
        }}
      />
    </Datagrid>
  );
};

export const SubscriptionList = () => {
  return (
    <List
      resource="subscriptions"
      sort={{ field: "updatedAt", order: "DESC" }}
      filters={subscriptionFilters}
      storeKey={false}
    >
      <SubscriptionDataGrid showEmail={true} />
    </List>
  );
};
const UserSubscriptionList = (props: any) => {
  return (
    <List
      resource="subscriptions"
      sort={{ field: "updatedAt", order: "DESC" }}
      filter={props.filter}
      exporter={false}
      storeKey={false}
      empty={<EmptySubscriptionList />}
    >
      <SubscriptionDataGrid showEmail={false} />
    </List>
  );
};

export default UserSubscriptionList;
